import type { CSSProperties } from 'react';
import { ICONS, MOVES } from '../lib/autoIcons';

/** Colours by meaning for icons that mark a kind of file or a state; otherwise an icon takes the text's colour. */
export type IconTone = 'pdf' | 'sheet' | 'word' | 'drawing' | 'image' | 'archive' | 'muted' | 'ok' | 'warn' | 'bad';

/**
 * An icon from the CRM's one family (lib/autoIcons.ts ICONS), drawn in the text's colour beside a label: used
 * instead of emoji, which look different on every device and can't follow dark mode. Inside a button or link it
 * moves the way its action works when hovered or focused (download drops, refresh turns…), unless less motion is
 * asked for. Decorative: the label next to it says what it is (pass `label` for an icon on its own).
 */
export function Icon({ name, tone, label }: { name: string; tone?: IconTone; label?: string }) {
  const i = ICONS[name] ?? ICONS.file!;
  const move = MOVES[name];
  return (
    <svg className={`ic${tone ? ` ic-${tone}` : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      style={move ? ({ '--ico-move': move } as CSSProperties) : undefined} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <path d={i.d} />
    </svg>
  );
}

export type DotTone = 'green' | 'yellow' | 'red' | 'grey';
/** A small coloured status dot beside a word that says the state (healthy, due soon, expired…). */
export function Dot({ tone }: { tone: DotTone }) {
  return <span className={`sdot sdot-${tone}`} aria-hidden="true" />;
}

/** The icon for a file, by its type: PDF red, Excel green, Word blue, drawings purple, pictures, archives. */
export function FileIcon({ name }: { name: string }) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return <Icon name="file" tone="pdf" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <Icon name="file" tone="sheet" />;
  if (['doc', 'docx'].includes(ext)) return <Icon name="file" tone="word" />;
  if (['dwg', 'dxf'].includes(ext)) return <Icon name="drawing" tone="drawing" />;
  if (['jpg', 'jpeg', 'png', 'heic', 'webp'].includes(ext)) return <Icon name="image" tone="image" />;
  if (['zip', 'rar', '7z'].includes(ext)) return <Icon name="archive" tone="archive" />;
  return <Icon name="file" tone="muted" />;
}
